import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "../api/client";

export type CampaignType =
  | "strategy"
  | "security_incident"
  | "it_ops"
  | "hr"
  | "finance"
  | "generic";

export type CampaignStatus = "active" | "escalated" | "resolving" | "closed";

export type CampaignSeverity = "low" | "medium" | "high" | "critical";

export interface SituationStage {
  label: string;
  state: "unreached" | "suspected" | "confirmed" | "resolved";
}

export interface Campaign {
  id: string;
  workspaceId: string;
  type: CampaignType;
  title: string;
  description: string | null;
  status: CampaignStatus;
  severity: CampaignSeverity | null;
  situationStages: SituationStage[];
  currentStageIndex: number;
  xiContextRef: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignListItem extends Campaign {
  playCount: number;
}

export interface CampaignPlayRow {
  id: string;
  archetype: string;
  state: string;
  campaignRole: "primary" | "secondary" | "support" | null;
  spawnedByStepId: string | null;
  createdAt: string;
}

export interface CampaignContextEntry {
  id: string;
  campaignId: string;
  key: string;
  value: unknown;
  confidence: "confirmed" | "suspected" | "unconfirmed";
  source: "step_output" | "analyst_input" | "brain_observation" | "external_event";
  sourcePlayId: string | null;
  sourceStepId: string | null;
  createdAt: string;
}

const listKey = (wsId: string, status?: string) =>
  ["gameplans", wsId, status ?? "default"] as const;
const detailKey = (wsId: string, id: string) =>
  ["gameplan", wsId, id] as const;
const contextKey = (wsId: string, id: string) =>
  ["gameplan-context", wsId, id] as const;

export function useGameplanList(
  workspaceId: string,
  status?: "active" | "closed" | "all",
) {
  return useQuery<{ items: CampaignListItem[] }, Error>({
    queryKey: listKey(workspaceId, status),
    enabled: !!workspaceId,
    queryFn: () => {
      const q = status ? `?status=${status}` : "";
      return apiGet<{ items: CampaignListItem[] }>(
        `/api/workspaces/${workspaceId}/gameplans${q}`,
      );
    },
  });
}

export function useGameplan(workspaceId: string, campaignId: string) {
  return useQuery<
    { campaign: Campaign; plays: CampaignPlayRow[] },
    Error
  >({
    queryKey: detailKey(workspaceId, campaignId),
    enabled: !!workspaceId && !!campaignId,
    queryFn: () =>
      apiGet(`/api/workspaces/${workspaceId}/gameplans/${campaignId}`),
  });
}

export function useGameplanContext(workspaceId: string, campaignId: string) {
  return useQuery<{ entries: CampaignContextEntry[] }, Error>({
    queryKey: contextKey(workspaceId, campaignId),
    enabled: !!workspaceId && !!campaignId,
    queryFn: () =>
      apiGet(
        `/api/workspaces/${workspaceId}/gameplans/${campaignId}/context`,
      ),
  });
}

export interface CreateCampaignBody {
  title: string;
  type: CampaignType;
  description?: string;
  severity?: CampaignSeverity;
  situationStages?: SituationStage[];
}

export function useCreateGameplan(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation<Campaign, Error, CreateCampaignBody>({
    mutationFn: (body) =>
      apiPost<Campaign>(`/api/workspaces/${workspaceId}/gameplans`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gameplans", workspaceId] });
    },
  });
}

export interface PatchCampaignBody {
  title?: string;
  description?: string | null;
  status?: CampaignStatus;
  severity?: CampaignSeverity | null;
  currentStageIndex?: number;
  situationStages?: SituationStage[];
}

export function usePatchGameplan(workspaceId: string, campaignId: string) {
  const qc = useQueryClient();
  return useMutation<Campaign, Error, PatchCampaignBody>({
    mutationFn: (body) =>
      apiPatch<Campaign>(
        `/api/workspaces/${workspaceId}/gameplans/${campaignId}`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: detailKey(workspaceId, campaignId) });
      qc.invalidateQueries({ queryKey: ["gameplans", workspaceId] });
    },
  });
}

export type AttachPlayBody =
  | {
      playId: string;
      campaignRole?: "primary" | "secondary" | "support";
      spawnedByStepId?: string;
    }
  | {
      templateKey: { industry: string; trade: string; archetype: string };
      campaignRole?: "primary" | "secondary" | "support";
      spawnedByStepId?: string;
    };

export function useAttachPlayToGameplan(
  workspaceId: string,
  campaignId: string,
) {
  const qc = useQueryClient();
  return useMutation<
    { playId: string; campaignId: string; campaignRole: string },
    Error,
    AttachPlayBody
  >({
    mutationFn: (body) =>
      apiPost(
        `/api/workspaces/${workspaceId}/gameplans/${campaignId}/plays`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: detailKey(workspaceId, campaignId) });
      qc.invalidateQueries({ queryKey: ["plays", workspaceId] });
    },
  });
}

export function useDetachPlayFromGameplan(
  workspaceId: string,
  campaignId: string,
) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { playId: string }>({
    mutationFn: ({ playId }) =>
      apiDelete(
        `/api/workspaces/${workspaceId}/gameplans/${campaignId}/plays/${playId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: detailKey(workspaceId, campaignId) });
      qc.invalidateQueries({ queryKey: ["plays", workspaceId] });
    },
  });
}

export interface AppendContextBody {
  key: string;
  value: unknown;
  confidence: "confirmed" | "suspected" | "unconfirmed";
}

export type CoachActionType =
  | "spawn_play"
  | "escalate_severity"
  | "observe"
  | "flag_human"
  | "noop";

export type CoachEventStatus =
  | "proposed"
  | "approved"
  | "rejected"
  | "auto_approved"
  | "executed"
  | "failed";

export interface CoachEvent {
  id: string;
  campaignId: string;
  trigger: string;
  triggerRefId: string | null;
  reasoning: string;
  actionType: CoachActionType;
  actionPayload: Record<string, unknown>;
  status: CoachEventStatus;
  approvedByUserId: string | null;
  approvedAt: string | null;
  executedAt: string | null;
  executionError: string | null;
  createdAt: string;
}

const coachKey = (wsId: string, cId: string, status: "proposed" | "all") =>
  ["gameplan-coach", wsId, cId, status] as const;

export function useCoachProposals(
  workspaceId: string,
  campaignId: string,
  status: "proposed" | "all" = "proposed",
) {
  return useQuery<{ items: CoachEvent[] }, Error>({
    queryKey: coachKey(workspaceId, campaignId, status),
    enabled: !!workspaceId && !!campaignId,
    queryFn: () =>
      apiGet(
        `/api/workspaces/${workspaceId}/gameplans/${campaignId}/coach-proposals?status=${status}`,
      ),
  });
}

export function useCoachApprove(workspaceId: string, campaignId: string) {
  const qc = useQueryClient();
  return useMutation<{ event: CoachEvent }, Error, { eventId: string }>({
    mutationFn: ({ eventId }) =>
      apiPost(
        `/api/workspaces/${workspaceId}/gameplans/${campaignId}/coach-proposals/${eventId}/approve`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gameplan-coach", workspaceId, campaignId] });
      qc.invalidateQueries({ queryKey: detailKey(workspaceId, campaignId) });
      qc.invalidateQueries({ queryKey: contextKey(workspaceId, campaignId) });
    },
  });
}

export function useCoachReject(workspaceId: string, campaignId: string) {
  const qc = useQueryClient();
  return useMutation<{ event: CoachEvent }, Error, { eventId: string }>({
    mutationFn: ({ eventId }) =>
      apiPost(
        `/api/workspaces/${workspaceId}/gameplans/${campaignId}/coach-proposals/${eventId}/reject`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gameplan-coach", workspaceId, campaignId] });
    },
  });
}

export function useCoachRun(workspaceId: string, campaignId: string) {
  const qc = useQueryClient();
  return useMutation<
    { events: CoachEvent[]; reasoning: string; skippedReason: string | null },
    Error,
    void
  >({
    mutationFn: () =>
      apiPost(
        `/api/workspaces/${workspaceId}/gameplans/${campaignId}/coach-proposals/run`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gameplan-coach", workspaceId, campaignId] });
      qc.invalidateQueries({ queryKey: detailKey(workspaceId, campaignId) });
      qc.invalidateQueries({ queryKey: contextKey(workspaceId, campaignId) });
    },
  });
}

export function useAppendContext(workspaceId: string, campaignId: string) {
  const qc = useQueryClient();
  return useMutation<CampaignContextEntry, Error, AppendContextBody>({
    mutationFn: (body) =>
      apiPost<CampaignContextEntry>(
        `/api/workspaces/${workspaceId}/gameplans/${campaignId}/context`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contextKey(workspaceId, campaignId) });
    },
  });
}

// ─── Members ─────────────────────────────────────────────────────────────
export interface CampaignMember {
  id: string;
  campaignId: string;
  userId: string;
  role: "lead" | "member";
  addedAt: string;
  addedByUserId: string;
}

const membersKey = (wsId: string, id: string) =>
  ["gameplan-members", wsId, id] as const;

export function useGameplanMembers(workspaceId: string, campaignId: string) {
  return useQuery<{ items: CampaignMember[] }, Error>({
    queryKey: membersKey(workspaceId, campaignId),
    enabled: !!workspaceId && !!campaignId,
    queryFn: () =>
      apiGet<{ items: CampaignMember[] }>(
        `/api/workspaces/${workspaceId}/gameplans/${campaignId}/members`,
      ),
  });
}

export function useAddGameplanMember(workspaceId: string, campaignId: string) {
  const qc = useQueryClient();
  return useMutation<
    CampaignMember,
    Error,
    { userId: string; role?: "lead" | "member" }
  >({
    mutationFn: (body) =>
      apiPost<CampaignMember>(
        `/api/workspaces/${workspaceId}/gameplans/${campaignId}/members`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: membersKey(workspaceId, campaignId) });
      // Member adds may newly reveal the campaign to the added user;
      // invalidate the list + detail so any open windows refresh.
      qc.invalidateQueries({ queryKey: ["gameplans", workspaceId] });
      qc.invalidateQueries({ queryKey: detailKey(workspaceId, campaignId) });
    },
  });
}

export function useRemoveGameplanMember(workspaceId: string, campaignId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (userId) =>
      apiDelete<void>(
        `/api/workspaces/${workspaceId}/gameplans/${campaignId}/members/${userId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: membersKey(workspaceId, campaignId) });
      qc.invalidateQueries({ queryKey: ["gameplans", workspaceId] });
      qc.invalidateQueries({ queryKey: detailKey(workspaceId, campaignId) });
    },
  });
}
